import React from 'react';
import { NavLink } from 'react-router-dom';
import { ll_url } from './utils/tools';
import { emailLink } from './utils/toolsReact';
import { toolName } from './settings';

export const spinnerIcon = <i className="pi pi-spin pi-spinner"/>
export const infoIcon = <i className="pi pi-info-circle text-blue-500" style={{ color: 'oklch(62.3% 0.214 259.815)' }} />
export const warnIcon = <i className="pi pi-exclamation-circle text-orange-400" style={{ color: 'oklch(75% 0.183 55.934)' }}/>
export const errorIcon = <i className="pi pi-exclamation-circle text-red-500" style={{ color: 'oklch(63.7% 0.237 25.331)' }} />

export const OPENING_DESCRIPTION = () => <>
    {toolName} is the web tool for fully automated and fast analysis of the effect of mutations on stability and function in known cancer targets applying in silico methods of molecular modelling, bioinformatics and machine learning. The tool navigates treatment by analysing mutations' effect on binding all FDA/EMA-approved drugs.
</>

export const PROTEIN_REQUEST = () => <>
    <p style={{ marginBottom: '0.4rem' }}>
        Do you miss any protein? Don't hesitate to contact us, we will do our best to provide the protein of your interest.
    </p>
    <p>E-mail: {emailLink('Target protein request')}</p>
</>

export const MUTATION_AT_ER = () => (
    <p>
        {warnIcon}
        <span style={{ marginLeft: '8px' }}>You selected a mutation at the position of essential residue. Please note that the mutation is likely to be deleterious. However, you can still submit the job to see the results.</span>
    </p>
)

export const MUTATION_NO_STRUCT_FOR_RESIDUE = () => (
    <p>
        {warnIcon}
        <span style={{ marginLeft: '8px' }}>Structural analyses will not be performed for this job as structural information does not exist for this position. However, you can still submit the job to see the sequence analysis results.</span>
    </p>
)

export const AMBIGUOUS_MUTATION = () => (
    <p style={{ marginBottom: '0.5rem' }}>
        {warnIcon}
        <span style={{ marginLeft: '8px' }}>The selected mutation applies to multiple isoforms. Please, make sure to choose the correct isoform. Otherwise, calculated research will be meaningless.</span>
    </p>
)

export const VIRTUAL_SCREENING_UNAVAILABLE = () => (
  <p style={{ marginTop: '0.5rem', marginBottom: '0.2rem' }}>
    {warnIcon}
    <span style={{ marginLeft: '8px' }}>Virtual screening of inhibitors is unavailable for this protein or for this particular position.</span>
  </p>
)

export const VIRTUAL_SCREENING_BARE = () => "Virtual screening of inhibitors will take several hours to several days, depending on server load."
export const VIRTUAL_SCREENING_INPUT_PAGE = () => (
  <p style={{ marginTop: '0.5rem', marginBottom: '0.2rem' }}>
    {warnIcon}
    <span style={{ marginLeft: '8px' }}>{VIRTUAL_SCREENING_BARE()}</span>
  </p>
)
export const VIRTUAL_SCREENING_SWITCH_ALERT = () => (
  VIRTUAL_SCREENING_BARE() + " Do you want to schedule it now?"
)

export const VIRTUAL_SCREENING_INFO_LATER = () => (
  <p style={{ marginTop: '0.5rem', marginBottom: '0.2rem' }}>
    {infoIcon}
    <span style={{ marginLeft: '8px' }}>Virtual screening can be enabled later.</span>
  </p>
)

export const LICENSE_MISSING = () => (
    <p>
        {warnIcon}
        <span style={{ marginLeft: '8px' }}>As a commercial user without a Rosetta or FoldX license, your calculation will be limited.</span>
    </p>
)

export const KNOWN_TARGETS_FAILED = () => <>
    <p style={{ marginBottom: '0.5rem' }}>
        {errorIcon}
        <span style={{ marginLeft: '8px' }}>An error occurred while loading proteins. Check your network connection or try again later.</span>
    </p>
    <p style={{ marginBottom: 0 }}>If the problem persists, please report this issue to {emailLink('Loading proteins failed')}.</p>
</>

export const SUBMISSION_FAILED = () => <>
    <p>
        {errorIcon}
        <span style={{ marginLeft: '8px' }}>An error occurred while submitting the job. Check your network connection or try again later.</span>
    </p>
    <p>If the problem persists, please report this issue to {emailLink()}.</p>
</>

export const JOB_INFO_FETCHING = () => (
    <p>
        {spinnerIcon}
        <span style={{ marginLeft: '8px' }}>Loading job information...</span>
    </p>
)

export const JOB_INFO_NOT_FOUND = (jobId: string) => (
    <p>
        {warnIcon}
        <span style={{ marginLeft: '8px' }}>Requested job with ID <em>{jobId}</em> does not exist. Please check your job ID or <NavLink to={ll_url('/')}>submit a new job</NavLink>.</span>
    </p>
)

export const JOB_INFO_FAILED = (jobId: string) => <>
    <p>
        {errorIcon}
        <span style={{ marginLeft: '8px' }}>An error occurred while loading job information. Check your network connection or try again later.</span>
    </p>
    <p>If the problem persists, please report this issue to {emailLink(`Loading job information for ${jobId} failed`)}.</p>
</>

export const JOB_RESULTS_NOT_FOUND = () => (
    <p>
        {spinnerIcon}
        <span style={{ marginLeft: '8px' }}>Your results are not yet available. This page will refresh automatically when any results are available.</span>
    </p>
)

export const JOB_RESULTS_LOADING = () => (
    <p>
        {spinnerIcon}
        <span style={{ marginLeft: '8px' }}>Loading results...</span>
    </p>
)

export const JOB_RESULTS_FAILED = (jobId: string) => <>
    <p>
        {errorIcon}
        <span style={{ marginLeft: '8px' }}>Loading results failed. Check your network connection or try again later.</span>
    </p>
    <p>If the problem persists, please report this issue to {emailLink(`Loading job results for ${jobId} failed`)}.</p>
</>

export const JOB_ONGOING = () => (
    <p>
        {spinnerIcon}
        <span style={{ marginLeft: '8px' }}>The page is automatically updated regularly to show the latest results.</span>
    </p>
)

export const JOB_NO_STRUCT_FOR_RESIDUE = () => (
    <p>
        {warnIcon}
        <span style={{ marginLeft: '8px' }}>It is not possible to perform this calculation for this mutation, as structural information does not exist for this particular position.</span>
    </p>
)

export const VIEWER_WAITING = () => (
    <p>
        {spinnerIcon}
        <span style={{ marginLeft: '8px' }}>The job is currently in progress. The viewer will be automatically updated once the job has been completed.</span>
    </p>
)

export const VIEWER_ANNOTATIONS_FETCHING = () => (
    <p>
        {spinnerIcon}
        <span style={{ marginLeft: '8px' }}>Loading protein annotations...</span>
    </p>
)

export const VIEWER_ANNOTATIONS_FAILED = (jobId: string) => (
    <>
        <p>
            {errorIcon}
            <span style={{ marginLeft: '8px' }}>An error occurred while loading protein annotations. Check your network connection or try again later.</span>
        </p>
        <p>If the problem persists, please report this issue to {emailLink(`Loading protein viewer annotations for ${jobId} failed`)}.</p>
    </>
)

export const VIEWER_PROTEIN_FAILED = (jobId: string, modelType: 'wild type' | 'mutant') => (
    <>
        <p>
            {errorIcon}
            <span style={{ marginLeft: '8px' }}>An error occurred while loading {modelType} model. Check your network connection or try again later.</span>
        </p>
        <p>If the problem persists, please report this issue to {emailLink(`Loading ${modelType} model for ${jobId} failed`)}.</p>
    </>
)

export const VIEWER_LIGANDS_FAILED = (jobId: string) => (
    <>
        <p>
            {errorIcon}
            <span style={{ marginLeft: '8px' }}>An error occurred while loading ligands. Check your network connection or try again later.</span>
        </p>
        <p>If the problem persists, please report this issue to {emailLink(`Loading ligands for ${jobId} failed`)}.</p>
    </>
)
